import React, { useContext, useState } from 'react';
import styled from 'styled-components';

import Badge from 'kingpin/atoms/Badge';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import Row from 'components/Common/Row';

import AiReadyEditor from './AiReadyEditor';
import { AiReadyContext } from './AiReadyProvider';
import ReportCounts from './ReportCounts';
import ReportDetail from './ReportDetail';
import {
  ISSUE_BADGES,
  LISTED_SUGGESTION_SEVERITIES,
  SEVERITY_LABELS,
} from './constants';

// What to fix, and what to add, in the order to do it.
//
// Only broken roster entries and reports worth adding. Everything else wrong
// with a flagged report is badged on its own row above and detailed in that
// row's "Fix issues" modal — listing it here as well meant reading the same
// finding twice and having two places to go and fix it.
//
// The opportunities are ranked greedily, so the projected figure on each row is
// cumulative: do the rows above it and this is where coverage lands. Reading any
// one row's "adds N metrics" in isolation would double-count.

const SectionDiv = styled.div`
  padding: 24px 32px;
  border-top: 1px solid var(--color-border-secondary);
`;

const CardDiv = styled.div`
  border: 1px solid var(--color-border-secondary);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 8px;
`;

const SuggestionCard = ({
  suggestion,
  report,
}: {
  suggestion: AiReady.Suggestion;
  // The analysed report this is about, when we have it. Present for every
  // suggestion in practice; typed optional so a lookup miss renders a slightly
  // thinner card rather than throwing.
  report?: AiReady.AnalysedReport;
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const isOpportunity = suggestion.kind === 'makeAiReady';

  return (
    <CardDiv data-testid={`ai-ready-suggestion-${suggestion.id}`}>
      <Row centerAlign spaceBetween>
        <Typography.Body type="Body 13 Bold">
          {/* An opportunity is just the report, named. Its headline restated
              the count already on the line below, and the projection only made
              sense read against every row above it — the greedy order still
              decides which five appear and in what sequence, it just no longer
              narrates itself. A finding keeps its headline: there the words ARE
              the finding. */}
          {isOpportunity
            ? suggestion.reportName
            : `${suggestion.reportName} — ${suggestion.headline}`}
        </Typography.Body>
        {isOpportunity && (
          <Row centerAlign style={{ gap: 8 }}>
            {/* Whether a report belongs in the agent's hands is decided by what
                is in it — which metrics, whether they are the account's blessed
                `core` picks, and what filters they carry. The counts line is
                the summary; this is the evidence. */}
            {report && (
              <Button
                type="Ghost"
                size="Small"
                label={isExpanded ? 'Hide details' : 'More details'}
                testId={`ai-ready-suggestion-detail-${suggestion.reportId}`}
                onClick={() => setIsExpanded((current) => !current)}
              />
            )}
            <Button
              type="Secondary"
              size="Small"
              label={isEditing ? 'Cancel' : 'Make AI-ready'}
              testId={`ai-ready-flag-${suggestion.reportId}`}
              onClick={() => setIsEditing((current) => !current)}
            />
          </Row>
        )}
      </Row>
      {/* An opportunity is described the same way the roster above describes a
          report — what is actually in it. A sentence about why flagging is good
          said nothing the headline had not, and nothing you could weigh one
          candidate against another with. A finding keeps its reason, because
          there the "why" is the whole point. */}
      {isOpportunity && report ? (
        <ReportCounts report={report} />
      ) : (
        <Typography.Body type="Body 12" color="var(--color-text-secondary)">
          {suggestion.reason}
        </Typography.Body>
      )}
      {isExpanded && report && <ReportDetail report={report} />}
      {isEditing && (
        <AiReadyEditor
          reportId={suggestion.reportId}
          reportName={suggestion.reportName}
          initialDomains={[]}
          initialDescription={''}
          isCurrentlyAiReady={false}
          onSaved={() => setIsEditing(false)}
        />
      )}
    </CardDiv>
  );
};

const SuggestionList = () => {
  const { analysed, suggestions } = useContext(AiReadyContext);

  // Warnings are deliberately not listed here (see the note above), so the
  // empty state has to test what is actually rendered — otherwise an account
  // whose only findings are warnings gets a heading with nothing under it.
  const listed = suggestions.filter((suggestion) =>
    LISTED_SUGGESTION_SEVERITIES.includes(suggestion.severity),
  );

  return (
    <SectionDiv data-testid="ai-ready-suggestions">
      <Typography.Header type="H4">Suggestions</Typography.Header>
      {listed.length === 0 && (
        <Typography.Body type="Body 13" color="var(--color-text-secondary)">
          Nothing to flag — no AI-ready report is broken, and no unflagged
          report would add a metric the account measures. Anything else worth
          tidying is on the report&apos;s own row above.
        </Typography.Body>
      )}
      {LISTED_SUGGESTION_SEVERITIES.map((severity) => {
        const group = listed.filter(
          (suggestion) => suggestion.severity === severity,
        );
        if (group.length === 0) {
          return null;
        }
        return (
          <div key={severity} style={{ marginTop: 16 }}>
            {/* Only the broken group is headed. "Worth adding · 5 item(s)"
                restated the section title and a number you can see by
                looking — the broken badge earns its place because it is the
                thing that is NOT just a suggestion. */}
            {severity !== 'opportunity' && (
              <Row centerAlign style={{ gap: 8, marginBottom: 8 }}>
                <Badge
                  badgeType={ISSUE_BADGES[severity]}
                  text={SEVERITY_LABELS[severity]}
                />
              </Row>
            )}
            {group.map((suggestion) => (
              <SuggestionCard
                key={suggestion.id}
                suggestion={suggestion}
                report={analysed.find(
                  (candidate) => candidate.reportId === suggestion.reportId,
                )}
              />
            ))}
          </div>
        );
      })}
    </SectionDiv>
  );
};

export default SuggestionList;
